import moment from 'moment';

import { getFeatureLabel } from './feature-labels';

// Différences champ par champ entre deux versions de la configuration d'un
// client, sur le modèle de la correction des envois AS400 (page « Statut
// API ») : on aplatit les deux objets, puis on compare chemin par chemin.

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const flatten = (value, prefix = '', result = {}) => {
  if (isPlainObject(value) && Object.keys(value).length > 0) {
    Object.entries(value).forEach(([key, child]) =>
      flatten(child, prefix ? `${prefix}.${key}` : key, result)
    );
  } else if (prefix) {
    result[prefix] = value;
  }
  return result;
};

/**
 * @returns {{ path: string, type: 'added'|'removed'|'changed', before?: any, after?: any }[]}
 */
export const computeJsonDiff = (before, after) => {
  const flatBefore = flatten(before ?? {});
  const flatAfter = flatten(after ?? {});
  const paths = new Set([
    ...Object.keys(flatBefore),
    ...Object.keys(flatAfter),
  ]);

  const diff = [];
  paths.forEach((path) => {
    const inBefore = Object.prototype.hasOwnProperty.call(flatBefore, path);
    const inAfter = Object.prototype.hasOwnProperty.call(flatAfter, path);

    if (inBefore && !inAfter) {
      diff.push({ path, type: 'removed', before: flatBefore[path] });
    } else if (!inBefore && inAfter) {
      diff.push({ path, type: 'added', after: flatAfter[path] });
    } else if (
      JSON.stringify(flatBefore[path]) !== JSON.stringify(flatAfter[path])
    ) {
      diff.push({
        path,
        type: 'changed',
        before: flatBefore[path],
        after: flatAfter[path],
      });
    }
  });

  return diff.sort((a, b) => a.path.localeCompare(b.path));
};

const FIELD_LABELS = {
  name: 'Nom du client',
  siret: 'SIRET',
  tva: 'N° de TVA',
  street: 'Adresse',
  zipcode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  is_email_sending_enabled: 'Envoi d’emails',
  is_sms_sending_enabled: 'Envoi de SMS',
  logo_url: 'Logo',
  features_v2: 'Options',
  pricing: 'Tarification',
  price_per_vehicle: 'Prix par véhicule',
  price_per_email: 'Prix par email',
  price_per_sms: 'Prix par SMS',
  price_per_signature: 'Prix par signature',
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'E-mail',
  phone_number: 'Téléphone',
  invitation_sent: 'Invitation envoyée',
  type: 'Type',
  file_name: 'Fichier',
  mime_type: 'Format',
  size: 'Taille',
};

/**
 * Libellé lisible d'un chemin : « Options › Étapes › Afficher l’heure
 * estimée › Activé » pour `features_v2.TOUR_STEP.DISPLAY_ETA.enabled`.
 */
export const getPathLabel = (path) => {
  const segments = path.split('.');
  const isFeature = segments[0] === 'features_v2';

  return segments
    .map((segment, index) =>
      isFeature && index > 0
        ? getFeatureLabel(segment)
        : FIELD_LABELS[segment] || getFeatureLabel(segment)
    )
    .join(' › ');
};

export const formatDiffValue = (value) => {
  if (value === undefined) return '—';
  if (value === null) return 'vide';
  if (value === true) return 'oui';
  if (value === false) return 'non';
  if (Array.isArray(value)) return `[${value.join(', ')}]`;
  if (isPlainObject(value)) return '{}';
  return typeof value === 'string' ? `« ${value} »` : String(value);
};

export const HISTORY_EVENT_LABELS = {
  CLIENT_CREATED: 'Création du client',
  CLIENT_UPDATED: 'Modification de la fiche',
  LOGO_UPDATED: 'Logo',
  VROOM_CONFIGURATION_CREATED: 'Configuration VROOM par défaut',
  DOCUMENT_TEMPLATE_ADDED: 'Template déposé',
  DOCUMENT_TEMPLATE_ARCHIVED: 'Template supprimé',
  ADMIN_USER_CREATED: 'Administrateur du client créé',
};

export const formatDateTime = (date) =>
  date ? moment(date).format('DD/MM/YYYY HH:mm') : '—';
